import React, { useState, useEffect } from "react";
import { InternationalTradeData, PercentageValue } from "./models/IntTradeData";
import LineChart from "../../components/LineChart/LineChart";
import DropdownFilter from "../../components/DropdownFilter/DropdownFilter";
import { InternationalTradeCategory } from "./models/IntTradeCategory";
import Flex from "../../components/Flex/Flex";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { capitalCase } from "capital-case";
import SmallPrint from "../../components/SmallPrint/SmallPrint";
import Singlestat from "../../components/Singlestat/Singlestat";
import { InternationalTradeSubCategory } from "./models/IntTradeSubCategory";
import { InternationalTradeRegion } from "./models/IntTradeRegion";
import { InternationalTradeCountry } from "./models/IntTradeCountry";
import Loading from "../../components/Loading/Loading";

import "./InternationalTrade.css";
import { getFilteredData } from ".";

type DataState = {
  datapoints: InternationalTradeData[];
  categories: InternationalTradeCategory[];
  subcategories: InternationalTradeSubCategory[];
  regions: InternationalTradeRegion[];
  countries: InternationalTradeCountry[];
};

type FilterState = {
  category: InternationalTradeCategory | null;
  subcategory: InternationalTradeSubCategory | null;
  region: InternationalTradeRegion | null;
  country: InternationalTradeCountry | null;
};

type ResultState = {
  datapoints: InternationalTradeData[];
  annualChange: PercentageValue | null;
  exportShare: PercentageValue | null;
};

function InternationalTrade({ data }: { data: DataState }) {
  let location = useLocation();
  let locationHash = queryString.parse(location.search);

  const [filter, setFilter] = useState<FilterState>({
    category: null,
    subcategory: null,
    region: null,
    country: null,
  });

  const [result, setResult] = useState<ResultState>({
    datapoints: [],
    annualChange: 0,
    exportShare: 0,
  });

  useEffect(() => {
    // set default filters. first category and first region
    setFilter({
      ...filter,
      category: data.categories[1], // engineering products
      region: data.regions[3], // europe
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const [filteredData, annualChange, exportShare] = getFilteredData(
      filter,
      data
    );

    setResult({
      datapoints: filteredData,
      annualChange: annualChange,
      exportShare: exportShare,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (data.datapoints.length === 0) {
    return <Loading />;
  }

  const exportOf = capitalCase(
    filter.subcategory?.name ?? filter.category?.name ?? ""
  );
  const exportTo = capitalCase(
    filter.country?.name ?? filter.region?.name ?? ""
  );
  const title = `Development of Swedish Exports${
    exportOf ? " of " + exportOf : ""
  }${exportTo ? " to " + exportTo : ""}`;

  return (
    <>
      <Flex cols={2} className="InternationalTrade-filters" alignBottom>
        <DropdownFilter
          title="Commodity group"
          placeholder="CATEGORY"
          onChange={(item) =>
            setFilter({ ...filter, category: item, subcategory: null })
          }
          value={filter.category}
          options={data.categories}
          extractTitle={(item) => item.name}
          extractValue={(item) => item.id.toString()}
          testId="category"
          emptyOption
          sort
        />

        <DropdownFilter
          placeholder="SUB CATEGORY"
          onChange={(item) => setFilter({ ...filter, subcategory: item })}
          value={filter.subcategory}
          options={data.subcategories.filter(
            (subcategory) => subcategory.categoryId === filter.category?.id
          )}
          extractTitle={(item) => item.name}
          extractValue={(item) => item.id?.toString()}
          disabled={filter.category === null}
          testId="subcategory"
          emptyOption
          sort
        />

        <DropdownFilter
          title="Market"
          placeholder="REGION"
          onChange={(item) =>
            setFilter({ ...filter, region: item, country: null })
          }
          value={filter.region}
          options={data.regions}
          extractTitle={(item) => item.name}
          extractValue={(item) => item.id?.toString()}
          testId="region"
          emptyOption
          sort
        />

        <DropdownFilter
          placeholder="MARKET"
          onChange={(item) => setFilter({ ...filter, country: item })}
          value={filter.country}
          options={data.countries.filter(
            (country) => country.regionId === filter.region?.id
          )}
          extractTitle={(item) => item.name}
          extractValue={(item) => item.id?.toString()}
          disabled={filter.region === null}
          testId="country"
          emptyOption
          sort
        />
      </Flex>

      <LineChart
        data={result.datapoints.map((d) => d.value) as number[]}
        labels={result.datapoints.map((d) => d.year.toString())}
        xLabel="YEARS"
        yLabel="VALUE (MSEK)"
        title={title}
        valueUnit="MSEK"
        download={locationHash.download === "1"}
        lineStyle={"straight"}
      />

      <Flex className="InternationalTrade-bottom" cols={3}>
        <SmallPrint>
          <p>* Source: SCB</p>
        </SmallPrint>

        <Singlestat
          title="Development, annual percentage change, 2018-2019:"
          value={
            result.annualChange && result.annualChange !== Infinity
              ? Math.round(Number(result.annualChange) * 10) / 10 + " %"
              : "--"
          }
          testId={"annual-change"}
        />

        <Singlestat
          title={`Share of total Swedish exports to ${exportTo} 2019:`}
          value={
            result.exportShare && result.exportShare !== Infinity
              ? Math.round(Number(result.exportShare) * 10) / 10 + " %"
              : "--"
          }
          testId="export-share"
        />
      </Flex>
    </>
  );
}

export default InternationalTrade;
